import React, { useEffect, useRef } from 'react';
import Header from '../../components/Header/Header';
import Image from '../../components/Image/Image';
import Footer from '../../components/Footer/Footer';
import FactsAndFigures from '../../components/FactsAndFigures/FactsAndFigures';
import './Home.css';

// Hook para verificar si el elemento está en pantalla
const useVisibility = (ref) => {
  const [isVisible, setIsVisible] = React.useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);

  return isVisible;
};

const FeatureItem = ({ icon, title, description }) => {
  const ref = useRef();
  const isVisible = useVisibility(ref);

  return (
    <div ref={ref} className={`feature-item ${isVisible ? 'visible' : ''}`}>
      <i className={icon}></i>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );
};

const TimelineItem = ({ duration, returnRate }) => {
  const ref = useRef();
  const isVisible = useVisibility(ref);

  return (
    <div ref={ref} className={`timeline-item ${isVisible ? 'visible' : ''}`}>
      <div className="timeline-content">
        <h4>{duration}</h4>
        <div className="timeline-line-vertical"></div>
        <p>Rentabilidad {returnRate} por año</p>
      </div>
      <div className="timeline-line-horizontal"></div>
    </div>
  );
};

const LocationItem = ({ imgSrc, title, description }) => {
  const ref = useRef();
  const isVisible = useVisibility(ref);

  return (
    <div ref={ref} className={`location-item ${isVisible ? 'visible' : ''}`}>
      <img src={imgSrc} alt={title} />
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );
};

const Home = () => {
  return (
    <div>
      <Header />
      <Image />
      <main className="main-content">
        <section className="key-features">
          <h2>Características Clave</h2>
          <div className="grid-container">
            <FeatureItem 
              icon="fas fa-trophy"
              title="Más de 150 Proyectos Realizados"
              description="Skylight Capital ha completado con éxito más de 150 proyectos, destacando nuestra experiencia y capacidad en el mercado inmobiliario."
            />
            <FeatureItem 
              icon="fas fa-briefcase"
              title="20 Años de Experiencia"
              description="Con 20 años de experiencia en el sector, ofrecemos soluciones de inversión seguras y rentables para nuestros clientes."
            />
            <FeatureItem 
              icon="fas fa-euro-sign"
              title="Capital Mínimo"
              description="Nuestro modelo de inversión requiere un capital mínimo de 400,000 euros, garantizando acceso a oportunidades exclusivas."
            />
          </div>
        </section>
        <section className="investment-info">
          <div className="investment-header">
            <h2>Modelos y Criterios de Inversión</h2>
          </div>
          <div className="investment-container">
            <div className="investment-models">
              <h3>Corto Plazo</h3>
              <p>(400,000 € - 1,000,000 €)</p>
              <div className="timeline">
                <TimelineItem duration="12 Meses" returnRate="15%" />
                <TimelineItem duration="24 Meses" returnRate="17%" />
              </div>
            </div>
            <div className="investment-criteria">
              <div className="criteria-item">
                <i className="fas fa-bullseye"></i>
                <p>Proyectos para la clase media en Barcelona, con un enfoque en la remodelación y construcción de departamentos o edificios. Rendimientos a corto y mediano plazo (4-24 meses).</p>
              </div>
            </div>
          </div>
        </section>
        <section className="locations">
          <h2>Ubicaciones</h2>
          <div className="locations-container">
            <LocationItem 
              imgSrc="https://vinocarreteraymanta.com/wp-content/uploads/2019/12/00_Barrio_Eixample_Bcn_03.jpg"
              title="Eixample"
              description="Una de las áreas más emblemáticas de Barcelona, conocida por su arquitectura modernista y su alta demanda inmobiliaria."
            />
            <LocationItem 
              imgSrc="https://offloadmedia.feverup.com/barcelonasecreta.com/wp-content/uploads/2021/06/16085126/sant-antoni-1-1024x696.jpg"
              title="Sant Antoni"
              description="Un barrio en crecimiento con una mezcla de tradición y modernidad, ideal para inversiones a largo plazo."
            />
            <LocationItem 
              imgSrc="https://estatics-nasia.dtibcn.cat/nasia-pro/media/2015%2C05%2Cavinguda-de-la-diagonal.jpg"
              title="Les Corts"
              description="Zona residencial y de negocios, conocida por su tranquilidad y su cercanía a importantes centros comerciales."
            />
            <LocationItem 
              imgSrc="https://www.costacruceros.com/content/dam/costa/costa-magazine/articles-magazine/heritage/sagrada-familia-barcelona_ar/sagrada-familia_d.jpg.image.1296.974.high.jpg"
              title="Sagrada Familia"
              description="Famosa por la basílica del mismo nombre, esta área es popular entre turistas y residentes por igual."
            />
            <LocationItem 
              imgSrc="https://ajuntament.barcelona.cat/sarria-santgervasi/sites/default/files/gallery/mercat-sant-gervasi.jpg"
              title="Sarriá"
              description="Un barrio exclusivo y tranquilo, perfecto para aquellos que buscan una inversión segura en un entorno de alta calidad."
            />
          </div>
        </section>
        <FactsAndFigures />
      </main>
      <Footer />
    </div>
  );
}

export default Home;
